import React, { useMemo } from 'react';

import AddLinkIcon from '@mui/icons-material/AddLink';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, IconButton, Link, List, ListItem, Stack, Tooltip, Typography } from '@mui/material';
import { Theme, darken, lighten, styled } from '@mui/material/styles';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { Bedbank } from '@luxuryescapes/contract-svc-bedbank';

import GridPagination from '~/components/Common/Elements/GridPagination';
import Spinner from '~/components/Common/Spinner';

import { getSlug } from '~/utils/slugUtils';

import { CoreQuery, Supplier, SupplierManageAction } from './type';

const getBackgroundColor = (color: string, theme: Theme, coefficient: number) => ({
  backgroundColor: darken(color, coefficient),
  ...theme.applyStyles('light', {
    backgroundColor: lighten(color, coefficient),
  }),
});

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  '& .supplier-data--blocked': {
    ...getBackgroundColor(theme.palette.error.main, theme, 0.7),
    '&:hover': {
      ...getBackgroundColor(theme.palette.error.main, theme, 0.6),
    },
    '&.Mui-selected': {
      ...getBackgroundColor(theme.palette.error.main, theme, 0.5),
      '&:hover': {
        ...getBackgroundColor(theme.palette.error.main, theme, 0.4),
      },
    },
  },
}));

interface Props {
  count: number;
  data: Array<Bedbank.ListSupplierProperty>;
  queries: CoreQuery;
  isPulling: Record<string, boolean>;
  refetchContent: (supplier: Supplier, externalId: string, pullFromForm: boolean) => void;
  openModal: (id: string, action: SupplierManageAction, masterPropertyId?: string) => void;
  onPageChange: (page: number, limit: number) => void;
  onSortChange: (orderBy: CoreQuery['orderBy'], sortBy: CoreQuery['sortBy']) => void;
}

export default function PropertiesTable({
  count,
  data,
  queries,
  isPulling,
  onPageChange,
  onSortChange,
  openModal,
  refetchContent,
}: Props) {
  const columns: Array<GridColDef> = useMemo(
    () => [
      {
        field: 'supplierPropertyId',
        headerName: 'Id',
        disableColumnMenu: true,
        flex: 1,
        sortable: true,
        display: 'flex',
      },
      {
        field: 'supplier',
        headerName: 'Supplier',
        disableColumnMenu: true,
        flex: 1,
        sortable: false,
        display: 'flex',
      },
      {
        field: 'hotelName',
        headerName: 'Name',
        disableColumnMenu: true,
        flex: 2,
        sortable: true,
        display: 'flex',
      },
      {
        field: 'category',
        headerName: 'Category',
        disableColumnMenu: true,
        flex: 2,
        sortable: false,
        display: 'flex',
      },
      {
        field: 'masterProperties',
        headerName: 'Master Properties',
        disableColumnMenu: true,
        flex: 5,
        sortable: false,
        display: 'flex',
        renderCell: ({ row }: GridRenderCellParams<Bedbank.ListSupplierProperty>) => (
          <Box>
            <List>
              {row.masterProperties.map((masterProperty) => (
                <ListItem key={masterProperty.id}>
                  <Typography
                    color={
                      masterProperty.state === 'suggestion'
                        ? 'info'
                        : masterProperty.state === 'deleted'
                        ? 'warning'
                        : 'success'
                    }
                  >
                    <Link href={`/bedbank/properties/${masterProperty.id}`} underline="hover" color="inherit">
                      {masterProperty.name}
                    </Link>{' '}
                    ({masterProperty.id}) ({masterProperty.supplier})
                  </Typography>
                  <Tooltip title="Preview on site">
                    <IconButton
                      size="small"
                      href={`${window.configs['LUX_CUSTOMER_PORTAL']}/partner/${getSlug(masterProperty.name)}/${
                        masterProperty.id
                      }?preview=true`}
                      target="_blank"
                      rel="noopener"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {row.allowContentActions.includes('detach') &&
                    ['mapped', 'deleted'].includes(masterProperty.state) && (
                      <Tooltip title="Detach">
                        <IconButton onClick={() => openModal(row.id, 'detach', masterProperty.id)}>
                          <LinkOffIcon fontSize="medium" />
                        </IconButton>
                      </Tooltip>
                    )}
                  {row.allowContentActions.includes('attach') && masterProperty.state === 'suggestion' && (
                    <Tooltip title="Attach">
                      <IconButton onClick={() => openModal(row.id, 'attach', masterProperty.id)}>
                        <AddLinkIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        ),
      },
      {
        field: 'countryCode',
        headerName: 'Address',
        disableColumnMenu: true,
        display: 'flex',
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<Bedbank.ListSupplierProperty>) => (
          <Box>
            <Box>
              Country Code: <b>{row.countryCode}</b>
            </Box>
            <Box>{row.address}</Box>
            <Box>Latitude: {row.latitude}</Box>
            <Box>Longitude: {row.longitude}</Box>
          </Box>
        ),
        flex: 2,
      },
      {
        field: 'rating',
        headerName: 'Details',
        disableColumnMenu: true,
        sortable: false,
        display: 'flex',
        renderCell: ({ row }: GridRenderCellParams) => (
          <Box>
            <Box>
              <Stack direction="row" spacing={0.5}>
                Multi Unit{' '}
                {row.multiUnit ? (
                  <CheckIcon color="success" fontSize="medium" />
                ) : (
                  <CloseIcon color="error" fontSize="medium" />
                )}
              </Stack>
            </Box>
            <Box>
              Star Rating <b>{row.rating}</b>
            </Box>
            <Box>
              Rank <b>{row.rank}</b>
            </Box>
            <Box>
              Guest Rating Count <b>{row.guestRatingCount}</b>
            </Box>
            <Box>
              Guest Rating Overall <b>{row.guestRatingOverall}</b>
            </Box>
            <Box>
              Number Of Rooms <b>{row.numberOfRooms}</b>
            </Box>
            <Box>
              Number Of Images <b>{row.numberOfImages}</b>
            </Box>
          </Box>
        ),
        flex: 3,
      },
      {
        field: 'allowContentActions',
        headerName: 'Actions',
        disableColumnMenu: true,
        flex: 1,
        sortable: false,
        display: 'flex',
        renderCell: ({ row }: GridRenderCellParams<Bedbank.ListSupplierProperty>) => (
          <Box>
            {!queries.filterByMapping && row.allowContentActions.includes('attach') && (
              <Box>
                <Tooltip title="Attach">
                  <IconButton onClick={() => openModal(row.id, 'attach')}>
                    <AddLinkIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {row.masterProperties.filter((x) => x.state === 'mapped').length === 0 &&
              row.allowContentActions.includes('master') && (
                <Box>
                  <Tooltip title="De Couple">
                    <IconButton onClick={() => openModal(row.id, 'master')}>
                      <InsertLinkIcon fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            {!queries.filterByMapping && (
              <Box>
                <Tooltip title="Mapping">
                  <IconButton
                    href={`/bedbank/properties?page=1&limit=100&orderBy=asc&sortBy=hotelName&filterById=${row.id}&filterByMapping=true#suppliers_properties`}
                    target="_blank"
                  >
                    <AutoFixHigh fontSize="medium" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {row.isUpdateBlocked && (
              <Box>
                <Tooltip title="Refetch content">
                  <IconButton
                    onClick={() => refetchContent(row.supplier, row.externalId, false)}
                    disabled={!!isPulling?.[`${row.supplier}-${row.externalId}`]}
                  >
                    {isPulling?.[`${row.supplier}-${row.externalId}`] ? (
                      <Spinner size={25} />
                    ) : (
                      <RefreshIcon fontSize="medium" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        ),
      },
    ],
    [openModal, refetchContent, isPulling, queries],
  );

  return (
    <>
      <StyledDataGrid
        columns={columns}
        disableRowSelectionOnClick
        getRowClassName={(params) => `supplier-data--${params.row.isUpdateBlocked ? 'blocked' : 'active'}`}
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.id}
        rows={data ?? []}
        pagination
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={{ page: queries.page - 1, pageSize: queries.limit }}
        sortModel={[{ field: queries.sortBy, sort: queries.orderBy }]}
        onPaginationModelChange={({ page, pageSize }) => onPageChange(page + 1, pageSize)}
        onSortModelChange={(s) =>
          onSortChange(s[0]?.sort ?? 'asc', (s[0]?.field ?? queries.sortBy) as CoreQuery['sortBy'])
        }
        paginationMode="server"
        sortingMode="server"
        rowCount={count}
        density="compact"
        slots={{ pagination: GridPagination }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
      />
    </>
  );
}
